<template>
  <v-dialog v-model="openDialog"
            :fullscreen="$vuetify.breakpoint.smAndDown"
            content-class="deliver-dialog"
            max-width="600"
            persistent lazy>
    <v-stepper v-model="currentStep">
      <v-stepper-header>
        <v-stepper-step :complete="currentStep > 1 || !missingBOL" step="1">
          Attach {{ $t('terms.bill-of-lading') }}
        </v-stepper-step>

        <v-divider />

        <v-stepper-step :complete="currentStep > 2" step="2">
          Delivery Information
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <v-card>
            <v-layout column class="px-3">
              <v-layout row>
                <v-flex xs9>
                  <v-text-field :label="billOfLadingNumberFieldLabel"
                                v-model="billOfLadingNumber"
                                :error-messages="errors.first('bolNumber', 'step1')"
                                :data-vv-as= "billOfLadingNumberFieldLabel"
                                data-vv-name="bolNumber"
                                data-vv-scope="step1"
                                v-validate.immediate="{ required: true, alpha_dash: true }">
                  </v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row>
                <v-flex :xs6="canSkipDeliveryDocuments" :xs12="!canSkipDeliveryDocuments">
                  <v-list-tile-action-text>Attach Delivery Document Photos</v-list-tile-action-text>
                  <files-upload-btn @upload="uploadBOL($event, true)"
                                    @fileCount="onFileListChange"
                                    class="px-5"
                                    btn-class="primary mx-0"
                                    :uploading-progress="uploadPercentage"
                                    multiple
                                    :disabled="errors.any('step1')" >
                  </files-upload-btn>
                </v-flex>
                <v-flex xs1/>
                <V-flex xs3 v-if="canSkipDeliveryDocuments">
                  <v-list-tile-action-text class="pb-5 mb-5">Or, Skip this right now</v-list-tile-action-text>
                  <v-btn class="mt-4 "
                         flat
                         color="primary"
                         @click="skipFileUploads"
                         :disabled="errors.any('step1')"
                  >
                    <v-icon>fast_forward</v-icon>
                    &nbsp;Skip
                  </v-btn>
                </V-flex>

              </v-layout>

            </v-layout>
            <v-card-actions>
              <v-spacer />
              <v-btn flat color="accent" @click="openDialog = false">
                Cancel
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <v-stepper-content step="2">
          <v-layout column class="px-3">
            <v-layout column class="pr-5">
              <v-float-field label="Animal Count"
                             :error-messages="errorMessages"
                             v-model="animalCount"/>
              <v-float-field :label="deliveryDialogWeightLabel" v-model="animalWeight"/>
            </v-layout>
            <v-layout column class="pr-5">
              <v-text-field v-model="trailerIdentifier" label="Trailer Identifier"></v-text-field>
              <v-text-field v-model="truckIdentifier" label="Truck Identifier"></v-text-field>
              <v-textarea v-model="deliveryNotes" label="Notes"></v-textarea>
            </v-layout>
          </v-layout>

          <v-btn :loading="loadingDeliveredBtn"
                 color="accent"
                 :disabled="!validAnimalCount"
                 @click="setDelivered">
            Delivered
          </v-btn>

          <v-btn flat @click="openDialog = false">Cancel</v-btn>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-dialog>
</template>

<script>
import _ from 'lodash';

import API from '@/api/index';
import VFloatField from '@/components/VFloatField';

import FilesUploadBtn from '@/components/FilesUploadBtn';
import * as types from '@/store/mutationTypes';
import unitsOfMeasurePlugin from '../../mixins/unitsOfMeasurePlugin';
import pluginMixin from '../../mixins/pluginMixin';
import companyPluginsMixin from '../../mixins/companyPluginsMixin';
import bookedLoadMixin from '../../mixins/bookedLoadMixin';

export default {
  name: 'DeliverLoadDialog',
  props: {
    value: {
      type: Boolean,
    },
    load: {
      type: Object,
      required: true,
    },
    company: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      animalCount: null,
      animalWeight: null,
      truckIdentifier: null,
      billOfLadingNumber: '',
      uploadPercentage: null,
      trailerIdentifier: null,
      deliveryNotes: null,
      openDialog: this.value,
      loadingDeliveredBtn: false,
      currentStep: 1,
      fileCount: 0,
    };
  },
  mixins: [
    pluginMixin,
    unitsOfMeasurePlugin,
    companyPluginsMixin,
    bookedLoadMixin,
  ],
  components: {
    VFloatField,
    FilesUploadBtn,
  },
  watch: {
    value() {
      this.openDialog = this.value;
      if (!this.value) {
        this.resetForm();
      }
    },
    openDialog(val) {
      if (val) {
        this.setDefaults();
      }
      this.$emit('input', this.openDialog);
    },
  },
  computed: {
    _() {
      return _;
    },
    missingBOL() {
      return _.get(this.load, 'billOfLading.status', 'MISSING') === 'MISSING';
    },
    errorMessages() {
      return this.validAnimalCount || this.animalCount === null ? null : ['Animal Count must be 1 or more'];
    },
    validAnimalCount() {
      return this.animalCount && this.animalCount > 0;
    },
    billOfLadingNumberFieldLabel() {
      return `${this.$t('terms.bill-of-lading')} Number`;
    },
    deliveryDialogWeightLabel() {
      return `Animal Weight in ${ this.siteWeightMeasureLabel }.`;
    },
    canSkipDeliveryDocuments() {
      const loadRequires = this.loadRequiresDeliveryPaperwork;
      const companyRequires = this.companyRequiresDeliveryPaperwork;
      const required = loadRequires == null ? companyRequires : loadRequires;

      const skippable = !required

      return skippable && this.fileCount == 0;
    },
    companyRequiresDeliveryPaperwork() {
      const legacy = this.companyPluginLegacyLoadRequirements(this.company)
      return _.get(legacy, 'requireDeliveryPaperwork', true);
    },
    loadRequiresDeliveryPaperwork() {
      return this.bookedLoadRequiresDeliveryDocument(this.load);
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  methods: {
    resetForm() {
      this.animalCount = null;
      this.animalWeight = null;
      this.fileCount = 0;
    },
    onFileListChange(event) {
      this.fileCount = event;
    },
    async skipFileUploads() {
      await this.uploadBOL([]);
    },
    async uploadBOL(base64Files) {
      try {
        const isValid = await this.$validator.validateAll('step1');

        if (isValid) {
          this.uploadPercentage = 1;

          const config = {
            onUploadProgress: (progressEvent) => {
              this.uploadPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            },
          };

          const doc = this.currentActivePlugins.locality.is_aus ? 'DeliveryDocket' : 'BillOfLading';
          // const doc = 'BillOfLading' || 'DeliveryDocket';

          await API.load.uploadBOL({
            billOfLadingBase64s: base64Files,
            billOfLadingNumber: this.billOfLadingNumber,
            loadId: this.load.humanId,
            doc: doc,
            config,
          });

          this.currentStep = 2;

          this.$emit('refresh');
        }
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.billOfLadingNumber = '';
        this.uploadPercentage = null;
        this.fileCount = 0;
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    setDefaults() {
      this.animalCount = _.get(this.load, 'pickup.animalCount', null);
      this.animalWeight = _.get(this.load, 'pickup.animalWeightPounds', null);
      this.truckIdentifier = localStorage.getItem('truckIdentifier') || null;
      this.trailerIdentifier = localStorage.getItem('trailerIdentifier') || null;
      this.fileCount = 0;

      this.currentStep = this.missingBOL ? 1 : 2;
    },
    async setDelivered() {
      this.loadingDeliveredBtn = true;
      try {
        const params = {
          loadId: this.load.humanId,
          dropoffInfo: {
            animalCount: this.animalCount,
            // pounds is known to be deprecated. once US/AU backends converge we'll remove
            animalWeightPounds: this.animalWeight,
            animalWeight: this.animalWeight,
            trailerIdentifier: this.trailerIdentifier,
            truckIdentifier: this.truckIdentifier,
            deliveryNotes: this.deliveryNotes,
            when: new Date().toISOString(),
          },
        };

        if (this.trailerIdentifier) {
          localStorage.setItem('trailerIdentifier', this.trailerIdentifier);
        }

        if (this.truckIdentifier) {
          localStorage.setItem('truckIdentifier', this.truckIdentifier);
        }

        await API.load.setDelivered(params);
        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Delivered successfully!',
        });

        this.openDialog = false;
        this.$emit('refresh');
      } catch ({ message }) {
        this.$store.dispatch('applications/setErrorNotification', {
          message,
        });
      } finally {
        this.loadingDeliveredBtn = false;
        this.openDialog = false;
      }
    },
  },
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/modals/DeliverLoadDialog.vue